import React from 'react';
import { useJobCenter } from './JobCenterContext';
import type { JobCenterJob } from './jobCenter.types';
import { apiCall } from '../../services/api.service';
import { toastError, toastInfo, toastSuccess } from '../../features/clu/utils/notify';
import { useEffectiveRole } from '../../hooks/useEffectiveRole';
import { DateTimeUtils } from '../../utils/datetimeUtils';

export function JobCenterPanel() {
  const { jobs, activeJobs, dismissJob } = useJobCenter();
  const { isSuperUser } = useEffectiveRole();

  const runningFirst = [...jobs].sort((a, b) => {
    const aActive = isActive(a) ? 0 : 1;
    const bActive = isActive(b) ? 0 : 1;
    if (aActive !== bActive) return aActive - bActive;
    return (DateTimeUtils.parse(b.last_updated_at)?.getTime() || 0) - (DateTimeUtils.parse(a.last_updated_at)?.getTime() || 0);
  });

  return (
    <div className="rounded-lg border border-line bg-surface shadow-xl">
      <div className="flex items-center justify-between border-b border-line px-4 py-3">
        <div>
          <p className="text-sm font-semibold text-ink">Job Center</p>
          <p className="text-xs text-ink-muted">
            {activeJobs.length > 0 ? `${activeJobs.length} running` : 'No running jobs'}
          </p>
        </div>
      </div>

      <div className="max-h-[60vh] overflow-auto">
        {runningFirst.length === 0 ? (
          <div className="px-4 py-6 text-sm text-ink-muted">No jobs yet.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {runningFirst.map((job) => (
              <li key={job.job_id} className="px-4 py-3">
                <JobRow job={job} onDismiss={() => dismissJob(job.job_id)} isSuperUser={isSuperUser} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** A run standing on a question. `status` is what hitl_pause writes; the
 *  `step` is the older signal the same writer leaves on the way. */
function isWaitingForYou(job: JobCenterJob): boolean {
  return job.status === 'paused' || job.step === 'paused_hitl';
}

function JobRow({ job, onDismiss, isSuperUser }: { job: JobCenterJob; onDismiss: () => void; isSuperUser: boolean }) {
  const active = isActive(job);
  const pct = clamp(job.progress);
  const waiting = active && isWaitingForYou(job);

  // 2026-09-07 (ANFP/COMET): the panel showed a paused run's question for
  // 24 minutes with nothing to press. The question lives as a card in a
  // parked turn of a conversation; this finds that conversation and opens
  // it, where the card comes back on screen (NG rehydrates pending
  // decisions on open).
  const handleAnswer = async () => {
    try {
      const res = await apiCall(`clu-ng/hitl/pending?job_id=${encodeURIComponent(job.job_id)}`, 'GET', null, false, { shortTimeout: 15000 });
      const item = res?.items?.[0];
      if (!item?.session_id) {
        toastInfo('Nothing to answer', 'This run is no longer waiting on a question.');
        return;
      }
      const agent = item.agent_id != null ? `&agent=${encodeURIComponent(String(item.agent_id))}` : '';
      window.location.assign(`/clu-ng?session=${encodeURIComponent(item.session_id)}${agent}`);
    } catch (e: any) {
      toastError('Could not open the question', String(e?.message || e));
    }
  };

  const handleCancel = async () => {
    try {
      toastInfo('Cancelling job', job.title || job.job_id);
      await apiCall(`jobs/v5/admin/cancel/${job.job_id}`, 'POST', null, false, { shortTimeout: 15000 });
      toastSuccess('Job cancelled', job.title || job.job_id);
    } catch (e: any) {
      const msg = String((e?.message || e) ?? '');
      const isTimeout = /timeout|120000|15000/i.test(msg);
      toastError(isTimeout ? 'Backend did not respond' : 'Failed to cancel job', isTimeout ? 'Request timed out. Backend may be blocked.' : msg);
    }
  };

  const handleDelete = async () => {
    try {
      toastInfo('Deleting job', job.title || job.job_id);
      // Short timeout so we fail fast when backend is blocked (e.g. redis-admin already timing out)
      await apiCall(`jobs/v5/admin/purge/${job.job_id}`, 'DELETE', null, false, { shortTimeout: 15000 });
      toastSuccess('Job deleted', job.title || job.job_id);
      onDismiss();
    } catch (e: any) {
      const msg = String((e?.message || e) ?? '');
      const isTimeout = /timeout|120000|15000/i.test(msg);
      toastError(
        isTimeout ? 'Backend did not respond' : 'Failed to delete job',
        isTimeout
          ? 'Request timed out. Job removed from list. Restart the backend and purge via Settings > Redis if it still appears.'
          : msg
      );
      // Remove from UI anyway so the list is usable even when backend is stuck
      onDismiss();
    }
  };

  const handleDownload = async () => {
    try {
      toastInfo('Preparing download', 'Fetching PDF file...');
      
      // Get auth headers for authenticated request
      const { UserSessionRegistry } = await import('../../stores/user-session.registry');
      const { config } = await import('../../config');
      
      const authHeaders = await UserSessionRegistry.getAuthHeaders({
        includeToken: true,
      });
      
      // Download PDF using fetch with authentication
      const downloadUrl = `${config.apiUrl}/api/admin/tools-audit/connectors-library/export-pdf/${job.job_id}/download`;
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: authHeaders,
      });
      
      if (!response.ok) {
        throw new Error(`Failed to download PDF: ${response.status} ${response.statusText}`);
      }
      
      // Get blob from response
      const blob = await response.blob();
      
      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Ecosystem_Library_Report_${job.job_id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toastSuccess('Download Complete', 'PDF downloaded successfully');
    } catch (e: any) {
      toastError('Failed to download PDF', String(e?.message || e));
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-ink" title={job.title}>
            {job.title}
          </p>
          <p className="truncate text-xs text-ink-muted" title={job.job_id}>
            <span className="font-mono">{job.job_id}</span>
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {waiting && (
            <button
              type="button"
              className="rounded-md bg-accent px-2 py-1 text-xs font-medium text-white hover:opacity-90"
              onClick={handleAnswer}
              title="Open the conversation where this run is asking"
            >
              Answer
            </button>
          )}
          {isSuperUser && (
            <>
              {active && (
                <button
                  type="button"
                  className="rounded-md border border-line px-2 py-1 text-xs text-ink hover:bg-surface-2"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
              )}
              <button
                type="button"
                className="rounded-md border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-2 py-1 text-xs text-red-700 dark:text-red-300 hover:bg-red-100 hover:dark:bg-red-900/40"
                onClick={handleDelete}
                title="Deletes job tracking/state. Does not rollback side effects created by the job."
              >
                Delete
              </button>
            </>
          )}
          {/* Show download button for completed PDF export jobs */}
          {!active && job.job_type === 'connectors_library_pdf_export' && job.status === 'completed' && (
            <button
              type="button"
              className="rounded-md border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 text-xs text-blue-700 dark:text-blue-300 hover:bg-blue-100 hover:dark:bg-blue-900/40"
              onClick={handleDownload}
              title="Download generated PDF report"
            >
              Download PDF
            </button>
          )}
          {!active && (
            <button
              type="button"
              className="rounded-md border border-line px-2 py-1 text-xs text-ink hover:bg-surface-2"
              onClick={onDismiss}
            >
              Dismiss
            </button>
          )}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between text-xs text-ink-muted">
          <span className="truncate">{formatStatus(job)}</span>
          <span className="ml-2 shrink-0 font-semibold text-ink">{pct}%</span>
        </div>
        <div className="mt-1 h-2 w-full rounded-full bg-surface-2">
          <div
            className={`h-2 rounded-full ${active ? (waiting ? 'bg-amber-500' : 'bg-blue-600') : job.status === 'failed' || job.status === 'cancelled' ? 'bg-red-600' : 'bg-green-600'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {(job.message || job.step) && (
        <p className="text-xs text-ink">
          <span className="font-semibold">{job.step || 'step'}:</span> {job.message || ''}
        </p>
      )}

      <p className="text-[11px] text-ink-subtle">
        Started: {DateTimeUtils.formatForUser(job.created_at)}
        {active && <> · Running for {formatDuration(Date.now() - (DateTimeUtils.parse(job.created_at)?.getTime() || Date.now()))}</>}
      </p>
      <p className="text-[11px] text-ink-subtle">
        Last update: {DateTimeUtils.formatForUser(job.last_updated_at)}
      </p>
    </div>
  );
}

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '0m';
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function isActive(job: JobCenterJob): boolean {
  // Same terminal set as JobCenterContext.isTerminal: a cancelled job used to
  // read as active here, keeping its Cancel button and "Running for …".
  return job.status !== 'completed' && job.status !== 'failed' && job.status !== 'cancelled';
}

function clamp(n: any): number {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(100, Math.round(v)));
}

function formatStatus(job: JobCenterJob): string {
  if (job.status === 'completed') return 'Completed';
  if (job.status === 'failed') return 'Failed';
  if (job.status === 'cancelled') return 'Cancelled';
  if (isWaitingForYou(job)) return 'Waiting for you';
  if (job.status === 'created') return 'Created';
  if (job.status === 'deferred') return 'Queued • portal busy';
  if (job.status === 'running' || job.status === 'processing' || job.status === 'in_progress') {
    const transport = job.transport === 'poll' ? 'Polling' : 'Live';
    return `Running • ${transport}`;
  }
  return job.status || 'Unknown';
}


